import React from 'react';
import styled from 'styled-components';
import { Body7 } from '@ui';
import { GRID_GAP, optionalCellText, ROW_PADDING, ROW_PADDING_COMFORTABLE, ROW_PADDING_COMPACT } from './columns';
import { companyLabel } from './format';
import { CORE_CELLS } from './coreCells';
import { MarkerCell } from './cellStyles';
import { MemberMarkerCell } from './MemberMarkerCell';
import { RowPayload } from './RowPayload';

// One row of the table, plus the reasoning disclosure that belongs to it.
//
// A row and whatever it opens are one block, which is why the separator sits under both rather than
// between them — and why this component owns the pair rather than the table interleaving them.
//
// React.memo'd, and it earns it now. Until useFeedTableView existed, the column arrays and the cell
// context were computed BELOW FeedRowsTable's two early returns, where no useMemo could reach them —
// so every one of this component's props was a fresh identity on every render and memo would have
// been decoration. The hook runs before any return, so they are memoized at last.
//
// What this buys: the table pages at a thousand rows, and pressing a sort header, typing in the
// filter box or opening one row's reasoning used to re-render every row's cells. Now only the rows
// whose own data changed do.

// A row and whatever it opens are one block, closed by a single rule: the reasoning disclosure
// belongs to the row above it, so the separator goes under both rather than between them.
const RowGroup = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};

  &:last-child {
    border-bottom: none;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  gap: ${GRID_GAP}px;
  align-items: center;
  padding: ${({ $compact }) => ($compact ? ROW_PADDING_COMPACT : ROW_PADDING_COMFORTABLE)}px ${ROW_PADDING}px;
`;

// A member this row never mentions still needs its column filled — the column exists because some
// other row named the alias. Looked up once: the same find used to run twice per marker cell.
const memberForColumn = (row, column) => row.members.find((m) => m.key === column.key) || null;

export const FeedTableRow = React.memo(
  ({
    row,
    coreColumns,
    optionalColumns,
    memberColumns,
    gridColumns,
    compact,
    cellContext,
    onDrillDown,
    canDrillDown,
    valueScale,
  }) => (
    <RowGroup>
      <Row $columns={gridColumns} $compact={compact}>
        {coreColumns.map((column) => (
          <React.Fragment key={column.key}>{CORE_CELLS[column.key](row, cellContext)}</React.Fragment>
        ))}
        {optionalColumns.map((column) => (
          <div key={column.key}>
            <Body7>{optionalCellText(row, column.key)}</Body7>
          </div>
        ))}
        {memberColumns.map((column) => {
          const member = memberForColumn(row, column);
          return (
            <MarkerCell key={column.key}>
              {/* Per-marker: a member whose feed can't be resolved is still pressable, but it promises
                an explanation rather than a target that would 422. */}
              <MemberMarkerCell
                input={member}
                onDrillDown={onDrillDown}
                isDrillable={canDrillDown(member)}
                valueScale={valueScale}
              />
            </MarkerCell>
          );
        })}
      </Row>
      {/* Only a single-input feed has a per-row payload; a multi-input row renders nothing here. */}
      <RowPayload payload={row.payload} rowLabel={companyLabel(row)} />
    </RowGroup>
  )
);
